import { StandaloneSearchBox } from "@react-google-maps/api";
import { FC, FocusEvent, useEffect, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import { useGoogleMaps } from "../../hooks/useGoogleMaps";
import useI18n from "../../hooks/useTranslations";
import { getProjectStatusValues, Project, ProjectStatus } from "../../http/types/projects";
import { getUILanguages, Language } from "../../types";
import SimpleTextModalDialog from "../configurations/SimpleTextModalDialog";
import Form from "../ui/Form";
import FormField from "../ui/FormField";
import Input from "../ui/Input";
import CustomGoogleMap from "../ui/maps/CustomGoogleMap";
import MapsContainer from "../ui/maps/MapsContainer";
import SectionCard from "../ui/SectionCard";

import { CustomSelect } from "../ui/select/CustomSelect";
import Textarea from "../ui/Textarea";
import styles from "./ProjectDetails.module.css";

interface ProjectDetailsProps {
  project?: Project;
  onSubmit: (body: ProjectDetailsFormBody) => Promise<void>;
  disabled?: boolean;
  isLoading?: boolean;
  isAdmin?: boolean;
  canEdit?: boolean;
}

export interface ProjectDetailsFormBody {
  name?: string;
  language?: Language;
  status?: ProjectStatus;
  description?: string;
  address?: string;
  city?: string;
  country?: string;
  coordinates?: string;
  company?: string;
  contactPerson?: string;
}
const zoomNoCoordinates = 16;
const zoomCoordinates = 18;

const ProjectDetails: FC<ProjectDetailsProps> = ({
  project,
  onSubmit,
  disabled,
  isLoading,
  isAdmin,
  canEdit,
}: ProjectDetailsProps) => {
  const i18n = useI18n();
  const searchBoxRef = useRef<google.maps.places.SearchBox | null>(null);
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [mapError, setMapError] = useState<boolean>(false);
  const map = useRef<google.maps.Map | null>(null);
  const currentMarker = useRef<google.maps.marker.AdvancedMarkerElement | null>(null);
  const [showContactPersonDialog, setShowContactPersonDialog] = useState<string | undefined>(undefined);

  const languages = getUILanguages(i18n.translation);

  const stati = getProjectStatusValues(i18n.translation);

  const onDblClick = useRef<((latLng: google.maps.LatLng) => void) | undefined>(undefined);

  const { isLoaded: mapIsLoaded, error } = useGoogleMaps();

  useEffect(() => {
    setMapError(!!error);
  }, [error]);

  const updateMarker = async (latLng: google.maps.LatLng | null, moveCamera: boolean) => {
    // const { AdvancedMarkerElement } = (await google.maps.importLibrary("marker")) as google.maps.MarkerLibrary;
    if (currentMarker.current) {
      currentMarker.current.map = null;
    }

    if (latLng) {
      currentMarker.current = new google.maps.marker.AdvancedMarkerElement({
        map: map.current,
        position: latLng,
      });

      moveCamera &&
        map.current?.moveCamera({
          center: latLng,
        });
    } else {
      currentMarker.current = null;
    }
  };

  const defaultValues: ProjectDetailsFormBody = {
    name: project?.name,
    address: project?.address,
    city: project?.city,
    country: project?.country,
    description: project?.description,
    language: languages.find((x) => x.value === project?.language),
    status: stati.find((x) => x.value === project?.status),
    company: project?.company,
    contactPerson: project?.contactPerson,
    coordinates: project?.coordinates,
  };

  const initLocation = () => {
    // console.log(isPageLoading, mapIsLoaded, map.current, defaultValues);
    if (isPageLoading && mapIsLoaded && map.current && defaultValues && project) {
      if (defaultValues.coordinates) {
        const pos = new google.maps.LatLng({
          lat: Number(defaultValues.coordinates.split(",")[0]),
          lng: Number(defaultValues.coordinates.split(",")[1]),
        });
        updateMarker(pos, true);
        map.current?.moveCamera({
          center: pos,
        });
      } else {
        navigator?.geolocation.getCurrentPosition(({ coords: { latitude: lat, longitude: lng } }) => {
          map.current?.moveCamera({
            center: new google.maps.LatLng({
              lat,
              lng,
            }),
          });
        });
      }
      setIsPageLoading(false);
    }
  };

  const onMapLoad = (mapInstance: google.maps.Map) => {
    map.current = mapInstance;
    initLocation();
  };

  useEffect(() => {
    initLocation();
  }, [mapIsLoaded, map.current, project?.id, defaultValues]);

  return (
    <div className={styles.layout}>
      <div className={styles.formColumn}>
        <Form<ProjectDetailsFormBody>
          defaultValues={defaultValues}
          mode="onBlur"
          disabled={disabled}
          // the onSubmit is only triggered by the button, which is visible only to admins
          onSubmit={isAdmin ? () => (formData) => onSubmit(formData) : undefined}
          submitText={i18n.translation.common.save}
        >
          {({ register, reset, formState: { errors }, control, setValue, trigger, handleSubmit }) => {
            useEffect(() => {
              project && reset(defaultValues);
            }, [project]);

            // this is the automatic save mechanism, active only for the non admin page, only if the user is allowed to edit
            const submit = () => {
              !isAdmin &&
                canEdit &&
                trigger().then((x) => {
                  x &&
                    handleSubmit((data) => {
                      onSubmit(data).then(() => {
                        reset(data);
                      });
                    })();
                });
            };

            const getAddress = (results: google.maps.GeocoderResult[] | google.maps.places.PlaceResult[]) => {
              const address = results[0]?.formatted_address;

              let city = null;
              let province = null;

              const components = results[0]?.address_components ?? [];

              const countryShort = components.find((x) => x.types.includes("country"))?.short_name;

              // Attempt to retrieve the postal_town for UK and Northern Ireland first
              const postalTown = components.find((x) => x.types.includes("postal_town"))?.long_name;

              if (postalTown) {
                // For UK and Northern Ireland, use postal_town as the city
                city = postalTown;
              } else {
                // Default pattern for other regions (EU, etc.)
                city = components.find((x) => x.types.includes("locality"))?.long_name;

                // Fallback to administrative_area_level_3 if city is not found
                if (!city) {
                  city = components.find((x) => x.types.includes("administrative_area_level_3"))?.long_name;
                }

                // Fallback to administrative_area_level_2 if city is still not found
                if (!city) {
                  city = components.find((x) => x.types.includes("administrative_area_level_2"))?.long_name;
                }

                // Fallback to administrative_area_level_1 if city is still not found
                if (!city) {
                  city = components.find((x) => x.types.includes("administrative_area_level_1"))?.long_name;
                }

                if (countryShort === "IT") {
                  province = components.find((x) => x.types.includes("administrative_area_level_2"))?.short_name;
                } else {
                  const aal1 =
                    components.find((x) => x.types.includes("administrative_area_level_1"))?.short_name ?? "";
                  const aal2 =
                    components.find((x) => x.types.includes("administrative_area_level_2"))?.short_name ?? "";
                  if (aal1 !== aal2) {
                    province = aal1?.length < aal2?.length ? aal1 : aal2;
                  }
                }
              }
              const cityProvince = province && city !== province ? `${city} (${province})` : city;

              const country = results[0]?.address_components?.find((x) => x.types.includes("country"))?.long_name;

              return {
                address,
                cityProvince,
                province,
                country,
              };
            };

            onDblClick.current = (latLng: google.maps.LatLng) => {
              const coords = `${latLng.lat().toFixed(7)},${latLng.lng().toFixed(7)}`;
              if (map.current) {
                const timeout = setTimeout(() => {
                  setIsPageLoading(true);
                }, 200);

                const geocoder = new google.maps.Geocoder();
                geocoder.geocode({ location: latLng }, (results, status) => {
                  if (status === google.maps.GeocoderStatus.OK && results) {
                    const address = getAddress(results);

                    setValue("city", address.cityProvince);
                    setValue("country", address.country);
                    setValue("address", address.address);
                    setValue("coordinates", coords);
                    updateMarker(latLng, false);
                  }

                  clearTimeout(timeout);
                  setIsPageLoading(false);
                  setMapError(
                    status !== google.maps.GeocoderStatus.OK && status !== google.maps.GeocoderStatus.ZERO_RESULTS,
                  );
                  submit();
                });
              }
            };

            return (
              <>
                <SectionCard title={i18n.translation.common.details}>
                  <div className={styles.fieldGrid}>
                    <FormField label={i18n.translation.common.name} error={errors.name}>
                      {({ labelId, isOptional, isInvalid }) => (
                        <Input
                          id={labelId}
                          isInvalid={isInvalid}
                          disabled={disabled || isLoading}
                          readOnly={!isAdmin && !canEdit}
                          {...register("name", {
                            required: !isOptional,
                            minLength: 1,
                          })}
                          onBlur={(e: FocusEvent<HTMLInputElement>) => {
                            setValue("name", e.target.value);
                            defaultValues.name !== e.target.value && submit();
                          }}
                        />
                      )}
                    </FormField>
                    <Controller
                      name="language"
                      control={control}
                      rules={{ required: true }}
                      defaultValue={defaultValues.language}
                      render={({ field, fieldState }) => (
                        <FormField label={i18n.translation.common.languages.singular} error={fieldState.error}>
                          {({ labelId }) => (
                            <CustomSelect
                              {...field}
                              inputId={labelId}
                              options={languages}
                              isDisabled={!mapIsLoaded || disabled || isLoading || (!isAdmin && !canEdit)}
                              isLoading={!mapIsLoaded || isLoading}
                              getOptionLabel={(x) => x.display}
                              onChange={(value) => {
                                setValue("language", value ?? undefined);
                                defaultValues.language?.value !== value?.value && submit();
                              }}
                            />
                          )}
                        </FormField>
                      )}
                    />
                    <Controller
                      name="status"
                      control={control}
                      rules={{ required: true }}
                      defaultValue={defaultValues.status}
                      render={({ field, fieldState }) => (
                        <FormField label={i18n.translation.common.status} error={fieldState.error}>
                          {({ labelId }) => (
                            <CustomSelect
                              {...field}
                              inputId={labelId}
                              options={stati}
                              isDisabled={!mapIsLoaded || disabled || isLoading || (!isAdmin && !canEdit)}
                              isLoading={!mapIsLoaded || isLoading}
                              getOptionLabel={(x) => x.display}
                              onChange={(value) => {
                                setValue("status", value ?? undefined);
                                defaultValues.language?.value !== value?.value && submit();
                              }}
                            />
                          )}
                        </FormField>
                      )}
                    />
                  </div>
                </SectionCard>

                <SectionCard title={i18n.translation.common.location}>
                  <div className={styles.sectionColumn}>
                    <FormField
                      label={i18n.translation.common.address}
                      error={errors.address}
                      isOptional
                      showHelp={
                        isAdmin || canEdit
                          ? {
                              type: "error",
                              onOpen: isLoading
                                ? undefined
                                : () => setShowContactPersonDialog(i18n.translation.common.address),
                            }
                          : undefined
                      }
                    >
                      {({ labelId, isOptional, isInvalid }) => {
                        const input = (
                          <Input
                            id={labelId}
                            placeholder=""
                            isInvalid={isInvalid}
                            disabled={disabled || isLoading}
                            readOnly={!isAdmin && !canEdit}
                            {...register("address", {
                              required: !isOptional,
                            })}
                            onBlur={(e: FocusEvent<HTMLInputElement>) => {
                              if (!e.target.value) {
                                updateMarker(null, false);
                                setValue("city", undefined);
                                setValue("country", undefined);
                                setValue("coordinates", undefined);
                              }
                              setValue("address", e.target.value);
                              defaultValues?.address !== e.target.value && submit();
                            }}
                          />
                        );

                        if (!canEdit && !isAdmin) {
                          return input;
                        }

                        return mapIsLoaded ? (
                          <div className={styles.standAloneSearchBox}>
                            <StandaloneSearchBox
                              onLoad={(ref) => (searchBoxRef.current = ref)}
                              onPlacesChanged={() => {
                                const places = searchBoxRef.current?.getPlaces();
                                if (places && places.length > 0) {
                                  const place = places[0];
                                  const location = place?.geometry?.location;
                                  if (location) {
                                    const coords = `${location?.lat().toFixed(7)},${location?.lng().toFixed(7)}`;
                                    map.current?.moveCamera({ center: location });
                                    updateMarker(location, true);

                                    const address = getAddress(places);

                                    setValue("city", address.cityProvince);
                                    setValue("country", address.country);
                                    setValue("address", address.address);
                                    setValue("coordinates", coords);
                                  }

                                  submit();
                                }
                              }}
                            >
                              {input}
                            </StandaloneSearchBox>
                          </div>
                        ) : (
                          <Input id={labelId} isInvalid={isInvalid} disabled />
                        );
                      }}
                    </FormField>
                    <div className={styles.fieldGrid}>
                      <FormField label={i18n.translation.common.city} error={errors.city} isOptional>
                        {({ labelId, isOptional, isInvalid }) => (
                          <Input
                            id={labelId}
                            placeholder=""
                            isInvalid={isInvalid}
                            disabled={disabled || isLoading}
                            readOnly={!isAdmin && !canEdit}
                            {...register("city", {
                              required: !isOptional,
                            })}
                            onBlur={(e: FocusEvent<HTMLInputElement>) => {
                              setValue("city", e.target.value);
                              defaultValues?.city !== e.target.value && submit();
                            }}
                          />
                        )}
                      </FormField>
                      <FormField label={i18n.translation.common.country} error={errors.country} isOptional>
                        {({ labelId, isOptional, isInvalid }) => (
                          <Input
                            id={labelId}
                            placeholder=""
                            isInvalid={isInvalid}
                            disabled={disabled || isLoading}
                            readOnly={!isAdmin && !canEdit}
                            {...register("country", {
                              required: !isOptional,
                            })}
                            onBlur={(e: FocusEvent<HTMLInputElement>) => {
                              setValue("country", e.target.value);
                              defaultValues?.country !== e.target.value && submit();
                            }}
                          />
                        )}
                      </FormField>
                    </div>
                  </div>
                </SectionCard>

                <SectionCard title={i18n.translation.common.company}>
                  <div className={styles.fieldGrid}>
                    <FormField label={i18n.translation.common.name} error={errors.company} isOptional>
                      {({ labelId, isOptional, isInvalid }) => (
                        <Input
                          id={labelId}
                          isInvalid={isInvalid}
                          disabled={disabled || isLoading}
                          readOnly={!isAdmin && !canEdit}
                          {...register("company", {
                            required: !isOptional,
                            minLength: 1,
                          })}
                          onBlur={(e: FocusEvent<HTMLInputElement>) => {
                            setValue("company", e.target.value);
                            defaultValues.company !== e.target.value && submit();
                          }}
                        />
                      )}
                    </FormField>
                    <FormField
                      label={i18n.translation.common.contactPerson}
                      error={errors.contactPerson}
                      isOptional
                      showHelp={
                        isAdmin || canEdit
                          ? {
                              type: "error",
                              onOpen: isLoading
                                ? undefined
                                : () => setShowContactPersonDialog(i18n.translation.common.contactPerson),
                            }
                          : undefined
                      }
                    >
                      {({ labelId, isOptional, isInvalid }) => (
                        <>
                          <Input
                            id={labelId}
                            isInvalid={isInvalid}
                            disabled={disabled || isLoading}
                            readOnly={!isAdmin && !canEdit}
                            {...register("contactPerson", {
                              required: !isOptional,
                              minLength: 1,
                            })}
                            onBlur={(e: FocusEvent<HTMLInputElement>) => {
                              setValue("contactPerson", e.target.value);
                              defaultValues.contactPerson !== e.target.value && submit();
                            }}
                          />
                          {!!showContactPersonDialog && (
                            <SimpleTextModalDialog
                              title={showContactPersonDialog}
                              text={{ value: i18n.translation.common.contactPersonWarning }}
                              isOpen={!!showContactPersonDialog}
                              onClose={() => setShowContactPersonDialog(undefined)}
                            />
                          )}
                        </>
                      )}
                    </FormField>
                  </div>
                </SectionCard>

                <SectionCard title={i18n.translation.common.description}>
                  <FormField error={errors.description} isOptional>
                    {({ labelId, isOptional, isInvalid }) => (
                      <Textarea
                        id={labelId}
                        isInvalid={isInvalid}
                        disabled={disabled || isLoading || (!isAdmin && !canEdit)}
                        rows={5}
                        {...register("description", { required: !isOptional })}
                        onBlur={(e: FocusEvent<HTMLTextAreaElement>) => {
                          setValue("description", e.target.value);
                          defaultValues.description !== e.target.value && submit();
                        }}
                      />
                    )}
                  </FormField>
                </SectionCard>
              </>
            );
          }}
        </Form>
      </div>
      <div className={styles.mapSurface}>
        <MapsContainer isLoading={(isPageLoading || isLoading) && !mapIsLoaded} isDisabled={disabled} error={mapError}>
          <CustomGoogleMap
            zoom={currentMarker.current ? zoomCoordinates : zoomNoCoordinates}
            onLoad={onMapLoad}
            options={{
              disableDoubleClickZoom: true,
              mapTypeId: "satellite",
              mapId: "PROJECT_MAP_ID",
            }}
            onDblClick={(e) => {
              if (isAdmin || canEdit) {
                e.stop();
                if (e.latLng) {
                  onDblClick.current?.(e.latLng);
                }
              }
            }}
          />
        </MapsContainer>
      </div>
    </div>
  );
};

export default ProjectDetails;
